import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { BaseFillLock } from '@/app/components/icons/BaseFillLock';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineLoading2 } from '@/app/components/icons/BaseLineLoading2';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { BaseLineReturn } from '@/app/components/icons/BaseLineReturn';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/app/components/ui/toggle-group';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn } from '@/app/utils';
import { customizeAdd, customizeGet, customizeRemove } from '../../apis';

const MAX_INPUT_LENGTH = 300;
const MAX_CUSTOM_VALUE = 20;

export enum PromptLibraryOptionType {
  Multiple = 'multiple',
  Single = 'single',
}

interface PromptLibraryOptionProps<T> {
  title: string;
  groupKey: T;
  options: string[];
  value: string[];
  onValueChange: (key: T, value: string[]) => void;
  required?: boolean;
  tip?: string;
  hasAddButton?: boolean;
  shouldUpgrade?: boolean;
  onOpenPlanDialog?: () => void;
}

export const PromptLibraryOption = <T extends string>({
  title,
  groupKey,
  options,
  value,
  onValueChange,
  required = false,
  tip,
  hasAddButton = false,
  shouldUpgrade = false,
  onOpenPlanDialog,
}: PromptLibraryOptionProps<T>) => {
  const t = useTranslations();
  const [inputValue, setInputValue] = useState('');
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [customizeValues, setCustomizeValues] = useState<string[]>([]);
  const userInfo = useStore((state) => state.userInfo);

  const { run: addNewValue, loading } = useFetch(
    () =>
      customizeAdd({
        body: {
          key: {
            module: 'create',
            function: 'pro_editor',
            name: groupKey,
          },
          value: inputValue.trim(),
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (!data?.items?.values) {
          toast({
            message:
              error?.reason === 'FieldItemCountOutOfRange'
                ? t('create.proMode.maxCustomValue', { length: MAX_CUSTOM_VALUE })
                : t('popup.dialog.error.unknownError.content'),
            type: 'error',
          });
          return;
        }

        setInputValue('');
        setIsInputVisible(false);
        if (data?.items?.values) {
          setCustomizeValues(data?.items?.values);
          // 添加到选中的值中
          const newValue = inputValue.trim();
          if (newValue && !value.includes(newValue)) {
            onValueChange(groupKey, [...value, newValue]);
          }
        }
      },
    },
  );

  const { run: deleteCustomValue, loading: deleteLoading } = useFetch(
    (customValue: string) =>
      customizeRemove({
        query: {
          'key.module': 'create',
          'key.function': 'pro_editor',
          'key.name': groupKey,
          value: customValue,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data }, [customValue]) => {
        if (!data?.items?.values) {
          toast({
            message: 'delete failed',
            type: 'error',
          });
          return;
        }

        setCustomizeValues(data.items.values);
        // Remove from selected values if present
        if (value.includes(customValue)) {
          onValueChange(
            groupKey,
            value.filter((item) => item !== customValue),
          );
        }
      },
    },
  );

  // 进入页面查询 customizeValues（仅登录用户）
  const { run: fetchCustomizeValues, data } = useFetch(
    () =>
      customizeGet({
        query: {
          'item.key.module': 'create',
          'item.key.function': 'pro_editor',
          'item.key.name': groupKey,
          type: 'list',
        },
      }),
    {
      manual: true,
    },
  );

  useEffect(() => {
    if (data?.data?.items?.values) {
      setCustomizeValues(data?.data?.items?.values);
    }
  }, [data]);

  useEffect(() => {
    if (!userInfo) {
      setCustomizeValues([]);
      return;
    }
    fetchCustomizeValues();
  }, [userInfo, fetchCustomizeValues]);

  const handleValueChange = (newValue: string[]) => {
    onValueChange(groupKey, newValue);
  };

  const handleAddButtonClick = () => {
    setIsInputVisible(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // 过滤掉全角和半角逗号
    const filteredValue = e.target.value.replace(/[,，]/g, '');
    setInputValue(filteredValue);
    if (filteredValue.length >= MAX_INPUT_LENGTH) {
      toast({
        message: t('create.proMode.maxInputLength', {
          length: MAX_INPUT_LENGTH,
        }),
        type: 'warning',
      });
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      addNewValue();
    }
  };

  const handleCancelInput = () => {
    setIsInputVisible(false);
    setInputValue('');
  };

  const handleDeleteCustomValue = (e: React.MouseEvent, option: string) => {
    if (deleteLoading) {
      return;
    }

    e.stopPropagation();
    deleteCustomValue(option);
  };

  return (
    <div>
      <div className="my-2 flex items-center gap-2">
        <span className="font-medium text-sm text-system-white">
          {title}
          {required && <span className="text-system-danger">*</span>}
        </span>
        <span className="text-system-white48 text-xs">{tip}</span>
      </div>
      <ToggleGroup
        type={PromptLibraryOptionType.Multiple}
        value={value}
        onValueChange={handleValueChange}
        className="flex flex-wrap gap-2"
      >
        {[...options, ...customizeValues].map((option) => (
          <ToggleGroupItem key={option} value={option} aria-label={option} className="group relative">
            <CustomTooltip trigger="hover" content={option} showOnlyOnOverflow={true} maxLines={1} sideOffset={10}>
              {option}
            </CustomTooltip>
            {/* 仅自定义的值可以被删除 */}
            {customizeValues.includes(option) && (
              <span
                className="-top-1.5 -right-1.5 absolute hidden size-5 cursor-pointer items-center justify-center rounded-full bg-system-white24 text-system-white backdrop-blur-sm hover:bg-system-white32 group-hover:flex"
                onClick={(e) => handleDeleteCustomValue(e, option)}
              >
                <BaseLineClose className="size-2.5" />
              </span>
            )}
          </ToggleGroupItem>
        ))}
        {isInputVisible && (
          <div className="relative">
            <Input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              autoFocus
              maxLength={MAX_INPUT_LENGTH}
              className="h-[30px] w-fit min-w-[260px] rounded-8 border-system-white32 bg-[#34373F] px-4 py-1.5 text-system-white text-xs caret-system-blue02"
            />
            {!inputValue && (
              <div className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-5 flex items-center gap-1 text-system-white24 text-xs">
                <span>{t('create.proMode.inputPlaceholder')}</span>
                <span className="rounded-4 border border-system-white12 bg-system-white/5 px-1">
                  <BaseLineReturn className="text-base" />
                </span>
              </div>
            )}
            {loading ? (
              <div className="absolute top-0 right-0 flex h-full w-10 animate-spin items-center justify-center">
                <BaseLineLoading2 className="text-base" />
              </div>
            ) : (
              <Button
                onClick={handleCancelInput}
                className="absolute top-0 right-2 flex h-full items-center justify-center px-2 text-system-white text-xs transition-colors hover:text-system-white64"
              >
                {t('create.proMode.cancel')}
              </Button>
            )}
          </div>
        )}
        {hasAddButton && !isInputVisible && (
          <CustomTooltip
            content={
              shouldUpgrade
                ? t.rich('create.proMode.unlockProMode', {
                    upgrade: (chunks) => (
                      <span className="cursor-pointer font-medium text-system-blue02" onClick={onOpenPlanDialog}>
                        {chunks}
                      </span>
                    ),
                  })
                : ''
            }
          >
            <button
              type="button"
              className={cn(
                'relative inline-flex items-center justify-center rounded-8 bg-system-neutral02 px-4 py-2 hover:bg-system-hover01',
                shouldUpgrade && 'group cursor-not-allowed border border-system-white36',
              )}
              onClick={shouldUpgrade ? undefined : handleAddButtonClick}
            >
              <BaseLinePlus className="text-sm group-hover:opacity-40" />
              {shouldUpgrade && (
                <BaseFillLock className="-right-1 -bottom-1 absolute text-sm text-system-white64 group-hover:text-system-white" />
              )}
            </button>
          </CustomTooltip>
        )}
      </ToggleGroup>
    </div>
  );
};
